// Numbered badges for the planner's multi-day stage ends: a yellow circle
// with a dark edge, carrying the day number. Drawn on a canvas with the number
// baked in (see hike_map/helpers/stageMarkerIcon.ts for why a separate
// text-field would draw out of order with the icon).

export const STAGE_BADGE_ICON_PREFIX = 'plan-stage-badge-';

/** Drawn at pixelRatio 2: 28 × 28 logical px. */
export const BADGE_PIXEL_RATIO = 2;
const LOGICAL_SIZE = 28;

const YELLOW = '#fddf00';
const DARK = '#17181d';

export interface BadgeLayout {
  /** Width and height of the (square) image. */
  size: number;
  center: number;
  /** The yellow circle, to the middle of its edge. */
  radius: number;
  edgeWidth: number;
  fontPx: number;
}

/** Canvas geometry for a badge carrying `label`, in canvas (physical) pixels. */
export function stageBadgeLayout(label: string | number, pixelRatio: number = BADGE_PIXEL_RATIO): BadgeLayout {
  const s = pixelRatio;
  const size = LOGICAL_SIZE * s;
  const edgeWidth = 2 * s;
  return {
    size,
    center: size / 2,
    // Edge fully inside the image, with a pixel spare for antialiasing.
    radius: size / 2 - edgeWidth / 2 - s,
    edgeWidth,
    fontPx: (String(label).length > 1 ? 12 : 14) * s,
  };
}

export function stageBadgeIconId(label: string | number): string {
  return `${STAGE_BADGE_ICON_PREFIX}${label}`;
}

function drawStageBadge(label: string | number) {
  const layout = stageBadgeLayout(label);
  const canvas = document.createElement('canvas');
  canvas.width = layout.size;
  canvas.height = layout.size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const { center, radius, edgeWidth } = layout;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fillStyle = YELLOW;
  ctx.fill();
  ctx.lineWidth = edgeWidth;
  ctx.strokeStyle = DARK;
  ctx.stroke();

  ctx.fillStyle = DARK;
  ctx.font = `bold ${layout.fontPx}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(label), center, center + BADGE_PIXEL_RATIO / 2);

  const imageData = ctx.getImageData(0, 0, layout.size, layout.size);
  return { width: layout.size, height: layout.size, data: new Uint8Array(imageData.data.buffer) };
}

/**
 * Registers a badge per distinct label that isn't on the map yet. A base-map
 * switch clears custom images, so this is re-checked every time the stage
 * markers are drawn.
 */
export function ensureStageBadgeIcons(map: any, labels: Array<string | number>): void {
  if (!map) return;
  Array.from(new Set(labels.map(String))).forEach((label) => {
    const id = stageBadgeIconId(label);
    if (map.hasImage(id)) return;
    const image = drawStageBadge(label);
    if (image) map.addImage(id, image, { pixelRatio: BADGE_PIXEL_RATIO });
  });
}
